/**
 * Tour Service
 * Handles API calls for tour progress tracking
 */

import axiosInstance from './axiosConfig';
import { TourProgress } from '../config/tours/tour.types';

interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
  timestamp: string;
}

/**
 * Get all tour progress for the current user
 */
export async function getAllTourProgress(): Promise<TourProgress[]> {
  const response = await axiosInstance.get<ApiResponse<TourProgress[]>>('/api/tours/progress');
  // Handle both wrapped {data: [...]} and direct array responses
  const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
  return data;
}

/**
 * Get progress for a specific tour
 */
export async function getTourProgress(tourId: string): Promise<TourProgress | null> {
  const response = await axiosInstance.get<ApiResponse<TourProgress | null>>(`/api/tours/progress/${tourId}`);
  return response.data?.data || null;
}

/**
 * Start a tour
 */
export async function startTour(tourId: string, version: number): Promise<TourProgress> {
  const response = await axiosInstance.post<ApiResponse<TourProgress>>(`/api/tours/progress/${tourId}/start`, {
    version,
  });
  return response.data?.data;
}

/**
 * Update current step in a tour
 */
export async function updateTourStep(tourId: string, currentStep: number): Promise<TourProgress | null> {
  const response = await axiosInstance.post<ApiResponse<TourProgress | null>>(`/api/tours/progress/${tourId}/step`, {
    currentStep,
  });
  return response.data?.data || null;
}

/**
 * Complete a tour
 */
export async function completeTour(tourId: string): Promise<TourProgress | null> {
  const response = await axiosInstance.post<ApiResponse<TourProgress | null>>(`/api/tours/progress/${tourId}/complete`);
  return response.data?.data || null;
}

/**
 * Skip a tour
 */
export async function skipTour(tourId: string, version?: number): Promise<TourProgress | null> {
  const response = await axiosInstance.post<ApiResponse<TourProgress | null>>(`/api/tours/progress/${tourId}/skip`, {
    version,
  });
  return response.data?.data || null;
}

/**
 * Reset tour progress for replay
 */
export async function resetTour(tourId: string): Promise<void> {
  await axiosInstance.post(`/api/tours/progress/${tourId}/reset`);
}

/**
 * Reset all tours for replay (bulk reset)
 */
export async function resetAllTours(): Promise<void> {
  await axiosInstance.post('/api/tours/progress/reset-all');
}

const tourService = {
  getAllTourProgress,
  getTourProgress,
  startTour,
  updateTourStep,
  completeTour,
  skipTour,
  resetTour,
  resetAllTours,
};

export default tourService;
